import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useGlobalData } from '../context/GlobalDataContext.jsx';

/** Fallback document title when meta has none (matches F7 app name in App.jsx). */
const DEFAULT_APP_NAME = 'My App';

/**
 * Map pathname to settings.meta page key (for route-specific OG/meta).
 * global.settings.meta has: home, promotion, vip, login, register, deposit, etc.
 */
function getMetaPageKey(pathname) {
  const p = (pathname || '').replace(/\/$/, '') || '/';
  if (p === '/' || p === '/home') return 'home';
  if (p === '/promotion') return 'promotion';
  if (p === '/vip-ranking') return 'vip';
  if (p === '/login') return 'login';
  if (p === '/register') return 'register';
  return null;
}

function getMetaValue(meta, ...keys) {
  if (!meta || typeof meta !== 'object') return null;
  for (const k of keys) {
    const v = meta[k];
    if (v != null && v !== '') return String(v);
  }
  return null;
}

function mergeMeta(baseMeta, pageMeta) {
  if (!baseMeta || typeof baseMeta !== 'object') return pageMeta || null;
  if (!pageMeta || typeof pageMeta !== 'object') return baseMeta;
  const merged = { ...baseMeta };
  for (const k of Object.keys(pageMeta)) {
    const v = pageMeta[k];
    if (v != null && v !== '') merged[k] = v;
  }
  return merged;
}

function keywordsContent(keywords) {
  if (keywords == null || keywords === '') return null;
  if (Array.isArray(keywords)) return keywords.filter(Boolean).join(', ');
  return String(keywords);
}

export default function MetaFromGlobal() {
  const { pathname } = useLocation();
  const { globalData } = useGlobalData();
  const globalSettingMeta =
    globalData != null ? globalData?.settings?.meta ?? null : null;
  const pageKey = getMetaPageKey(pathname);
  const pageMeta = pageKey && globalSettingMeta?.[pageKey];
  const headMeta = mergeMeta(globalSettingMeta, pageMeta);

  const thumbnail =
    headMeta?.thumbnail ||
    getMetaValue(headMeta, 'og_image', 'og:image', 'image');
  const keywordsStr = headMeta?.keywords != null
    ? keywordsContent(headMeta.keywords)
    : null;

  if (globalSettingMeta == null) {
    return null;
  }

  return (
    <Helmet>
      <title>{`${headMeta?.title || DEFAULT_APP_NAME}`}</title>
      <meta
        name="description"
        content={headMeta?.description || 'No description provided'}
      />

      {thumbnail && (
        <>
          <link rel="icon" href={thumbnail ? thumbnail : ''} />
          <link rel="apple-touch-icon" href={thumbnail} />
        </>
      )}

      <meta
        property="og:title"
        content={headMeta?.og_title || headMeta?.title || ''}
      />
      <meta
        property="og:description"
        content={headMeta?.og_description || headMeta?.description || ''}
      />
      {(headMeta?.og_image || thumbnail) && (
        <meta property="og:image" content={headMeta.og_image || thumbnail} />
      )}
      {getMetaValue(headMeta, 'og_type', 'og:type') && (
        <meta
          property="og:type"
          content={getMetaValue(headMeta, 'og_type', 'og:type')}
        />
      )}
      {getMetaValue(headMeta, 'og_url', 'og:url') && (
        <meta
          property="og:url"
          content={getMetaValue(headMeta, 'og_url', 'og:url')}
        />
      )}

      {keywordsStr && <meta name="keywords" content={keywordsStr} />}

      {(headMeta?.canonical_url || getMetaValue(headMeta, 'og_url', 'og:url')) && (
        <link
          rel="canonical"
          href={headMeta?.canonical_url || getMetaValue(headMeta, 'og_url', 'og:url')}
        />
      )}

      {getMetaValue(headMeta, 'twitter_card', 'twitter:card') && (
        <meta
          name="twitter:card"
          content={getMetaValue(headMeta, 'twitter_card', 'twitter:card')}
        />
      )}
      {getMetaValue(headMeta, 'twitter_title', 'twitter:title', 'title') && (
        <meta
          name="twitter:title"
          content={getMetaValue(headMeta, 'twitter_title', 'twitter:title', 'title')}
        />
      )}
      {(headMeta?.twitter_description ||
        headMeta?.og_description ||
        headMeta?.description) && (
        <meta
          name="twitter:description"
          content={
            getMetaValue(
              headMeta,
              'twitter_description',
              'twitter:description',
              'og_description',
              'og:description',
              'description'
            ) || ''
          }
        />
      )}
    </Helmet>
  );
}
